import { useState } from "react";
import { MapPin, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useSharedLocation } from "@/context/LocationContext";

// Location box + radius slider bound to the session-wide center point.
export default function LocationFilterBar({ testPrefix, noun }) {
  const { loc, setLoc, radius, setRadius, locating, denied, locateMe, setManual } = useSharedLocation();
  const [text, setText] = useState("");
  const t = (s) => `${testPrefix}-${s}`;
  return (
    <div className="bg-white/80 border border-sage/20 rounded-3xl p-5 sm:p-6 mb-8 shadow-sm" data-testid={t("location-filter")}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setManual(text)}
            placeholder="Your suburb or area, e.g. Roodepoort" className="pl-9 rounded-full bg-softstone/60 border-sage/30"
            data-testid={t("location-input")} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none" onClick={() => setManual(text)}
            disabled={locating || text.trim().length < 3} data-testid={t("set-location-btn")}>Set</Button>
          <Button variant="outline" className="rounded-full shrink-0 flex-1 sm:flex-none border-sage/40" onClick={() => locateMe()}
            disabled={locating} data-testid={t("use-location-btn")}>
            <Navigation className="w-4 h-4 mr-1" /> {locating ? "Locating..." : "Use my location"}
          </Button>
        </div>
      </div>
      {denied && <LocationDeniedNote testid={t("location-denied")} />}
      {loc ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-semibold text-forest/60 mb-2">
            <span data-testid={t("current-location-label")}>Near: {loc.label}</span>
            <span className="text-forest" data-testid={t("radius-value")}>Within {radius} km</span>
          </div>
          <Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={5} max={100} step={5} className="w-full" data-testid={t("radius-slider")} />
          <div className="flex justify-between text-[10px] text-forest/40 mt-1"><span>5 km</span><span>100 km</span></div>
          <button onClick={() => setLoc(null)} className="text-xs font-bold text-terracotta-dark hover:underline mt-2" data-testid={t("clear-location-btn")}>
            Clear location filter
          </button>
        </div>
      ) : !denied && (
        <p className="text-xs text-forest/50 mt-3">Set your location to see {noun} near you — the distance slider defaults to 20 km and goes up to 100 km.</p>
      )}
    </div>
  );
}

export function LocationDeniedNote({ testid }) {
  const { setManual, locating } = useSharedLocation();
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); setManual(text); };
  return (
    <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm" data-testid={testid}>
      <label htmlFor={`${testid}-input`} className="text-forest/60 shrink-0">Enter your area or suburb</label>
      <div className="flex gap-2 flex-1">
        <Input id={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Roodepoort"
          className="h-9 rounded-full bg-white border-sage/30 flex-1" data-testid={`${testid}-input`} />
        <Button type="submit" size="sm" variant="outline" className="rounded-full border-sage/40" disabled={locating || text.trim().length < 3}
          data-testid={`${testid}-submit`}>{locating ? "Finding..." : "Find nearby"}</Button>
      </div>
    </form>
  );
}

export function DistanceBadge({ km, testid }) {
  if (km == null) return null;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full" data-testid={testid}>
      {km} km away
    </span>
  );
}

export function UnknownLocationHeading({ testid }) {
  return (
    <h3 className="col-span-full text-xs font-black uppercase tracking-wide text-forest/50 mt-4 border-t border-sage/20 pt-4" data-testid={testid}>
      Location not specified
    </h3>
  );
}
